import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ChatSoundControl from './ChatSoundControl';
import './ChatControls.css';
import { useChatPresenceStatus } from './useChatPresence';

function keyBytes(key) {
  const raw = atob(key.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}
function bounded(promise, ms = 12000) {
  let timer;
  const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Push-Vorbereitung dauert zu lange. Bitte erneut prüfen.')), ms); });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
const englishText = {
  "Push-Vorbereitung dauert zu lange. Bitte erneut prüfen.": "Push setup timed out. Please try again.",
  "Push wird geprüft…": "Checking push…",
  "Push-Service konnte nicht gestartet werden.": "The push service could not start.",
  "Push-Service konnte nicht aktiviert werden.": "The push service could not be activated.",
  "Push ist auf dem Server noch nicht aktiv. Der Installer muss die Push-Schlüssel einrichten und das Backend neu starten.": "Push is not configured on the server yet. Please contact management.",
  "Push ist auf diesem Gerät aktiv.": "Push is enabled on this device.",
  "Neue Chat-Nachrichten auch bei geschlossener App erhalten.": "Receive new chat notifications even when the app is closed.",
  "Push-Endpunkt fehlt. Bitte Backend-Installation und Neustart prüfen.": "The push service is missing on the server. Please contact management.",
  "Push konnte nicht vorbereitet werden.": "Push could not be set up.",
  "Push wurde für Aeonas blockiert. In den Mitteilungseinstellungen für Aeonas bzw. in den Website-Einstellungen des Browsers erlauben und diese Ansicht neu öffnen.": "Notifications for Aeonas are blocked. Enable them in the app or browser notification settings, then reopen this screen.",
  "Push auf diesem Gerät deaktiviert.": "Push is disabled on this device.",
  "Benachrichtigungen wurden nicht erlaubt. Du kannst sie in den App- oder Browser-Einstellungen freigeben.": "Notifications were not allowed. You can enable them in the app or browser settings.",
  "Push ist auf diesem Gerät aktiv. Zum Test eine Nachricht von einem anderen Konto senden.": "Push is enabled. To test it, ask someone to send a message from another account.",
  "Push konnte nicht aktiviert werden.": "Push could not be enabled.",
  "Push einrichten": "Set up push",
  "Bitte warten…": "Please wait…",
  "Push erneut prüfen": "Check push again",
  "Push deaktivieren": "Disable push",
  "Push aktivieren": "Enable push",
  "Push ist in dieser Ansicht nicht verfügbar. Auf dem iPhone Aeonas zum Home-Bildschirm hinzufügen.": "Push is unavailable in this view. On iPhone, add Aeonas to the Home Screen with Open as Web App enabled.",
  "Push ist auf dem Server noch nicht eingerichtet": "Push is not configured on the server yet. Please contact management.",
  "Ungültiges Push-Abonnement": "The push subscription is invalid.",
  "Push konnte nicht gespeichert werden": "The push subscription could not be saved.",
  "Push-Status nicht verfügbar": "Push status is unavailable.",
  "Push konnte nicht deaktiviert werden": "Push could not be disabled.",
  "Nicht eingeloggt": "Please log in again.",
  "Session ungültig": "Your session has expired. Please log in again."
};
export default function ChatPushSettings() {
  const foregroundStatus = useChatPresenceStatus();
  const english = true;
  const text = value => english ? englishText[value] || value : value;
  const installed = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
  const supported = window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const [workerStatus, setWorkerStatus] = useState('pending');
  const [serverStatus, setServerStatus] = useState('pending');
  const [registration, setRegistration] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [phase, setPhase] = useState(supported ? 'loading' : 'unsupported');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [instructions, setInstructions] = useState(false);
  const [config, setConfig] = useState(null);
  const mounted = useRef(false);
  const attempt = useRef(0);
  const prepare = useCallback(async () => {
    const generation = ++attempt.current;
    setPhase('loading'); setNotice('Push wird geprüft…');
    setWorkerStatus('pending'); setServerStatus('pending');
    try {
      const [reg, response] = await bounded(Promise.all([
        navigator.serviceWorker.register('/aeonas-push-sw.js', { scope: '/' }).then(async reg => {
          if (!reg.active) {
            const worker = reg.installing || reg.waiting;
            if (!worker) throw new Error('Push-Service konnte nicht gestartet werden.');
            await new Promise((resolve, reject) => {
              const changed = () => {
                if (worker.state === 'activated' || worker.state === 'redundant') {
                  worker.removeEventListener('statechange', changed);
                  if (worker.state === 'activated') resolve();
                  else reject(new Error('Push-Service konnte nicht aktiviert werden.'));
                }
              };
              worker.addEventListener('statechange', changed); changed();
            });
          }
          if (mounted.current && generation === attempt.current) setWorkerStatus('ready');
          return reg;
        }),
        axios.get('/api/chat/push/config', { timeout: 10000 }).then(response => {
          if (mounted.current && generation === attempt.current) setServerStatus(response.data?.enabled ? 'ready' : 'unconfigured');
          return response;
        }).catch(error => {
          if (mounted.current && generation === attempt.current) setServerStatus('error');
          throw error;
        }),
      ]));
      if (!mounted.current || generation !== attempt.current) return;
      if (!response.data?.enabled || !response.data.publicKey) {
        setConfig(null); setPhase('unconfigured');
        setNotice('Push ist auf dem Server noch nicht aktiv. Der Installer muss die Push-Schlüssel einrichten und das Backend neu starten.'); return;
      }
      const sub = await bounded(reg.pushManager.getSubscription());
      const status = sub ? await axios.get('/api/chat/push/subscription', { params: { endpoint: sub.endpoint }, timeout: 10000 }) : null;
      if (!mounted.current || generation !== attempt.current) return;
      setRegistration(reg); setConfig(response.data);
      setEnabled(Boolean(status?.data?.subscribed)); setPhase('ready');
      window.dispatchEvent(new Event('aeonas-push-changed'));
      setNotice(status?.data?.subscribed ? 'Push ist auf diesem Gerät aktiv.' : 'Neue Chat-Nachrichten auch bei geschlossener App erhalten.');
    } catch (e) {
      if (!mounted.current || generation !== attempt.current) return;
      setPhase('error');
      setNotice(e.response?.data?.message || (e.response?.status === 404 ? 'Push-Endpunkt fehlt. Bitte Backend-Installation und Neustart prüfen.' : e.message || 'Push konnte nicht vorbereitet werden.'));
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    if (supported) void prepare();
    return () => { mounted.current = false; attempt.current += 1; };
  }, [supported, prepare]);
  const toggle = async () => {
    if (!supported) { setInstructions(current => !current); return; }
    if (busy || phase === 'loading') return;
    if (phase !== 'ready' || !registration || !config) { void prepare(); return; }
    if (!enabled && Notification.permission === 'denied') {
      setNotice('Push wurde für Aeonas blockiert. In den Mitteilungseinstellungen für Aeonas bzw. in den Website-Einstellungen des Browsers erlauben und diese Ansicht neu öffnen.'); return;
    }
    setBusy(true); setNotice('');
    try {
      if (enabled) {
        const sub = await bounded(registration.pushManager.getSubscription());
        if (sub) {
          await axios.delete('/api/chat/push/subscription', { data: { endpoint: sub.endpoint }, timeout: 10000 });
          await bounded(sub.unsubscribe());
        }
        setEnabled(false); setNotice('Push auf diesem Gerät deaktiviert.');
      } else {
        // Permission is requested directly during the click, before any network await.
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') throw new Error('Benachrichtigungen wurden nicht erlaubt. Du kannst sie in den App- oder Browser-Einstellungen freigeben.');
        const sub = await bounded(registration.pushManager.getSubscription()) || await bounded(registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(config.publicKey) }));
        await axios.post('/api/chat/push/subscription', sub.toJSON(), { timeout: 10000 });
        setEnabled(true); window.dispatchEvent(new Event('aeonas-push-changed')); setNotice('Push ist auf diesem Gerät aktiv. Zum Test eine Nachricht von einem anderen Konto senden.');
      }
    } catch (e) { setNotice(e.response?.data?.message || e.message || 'Push konnte nicht aktiviert werden.'); }
    finally { setBusy(false); }
  };
  const label = !supported ? 'Push einrichten' : busy ? 'Bitte warten…' : phase === 'loading' ? 'Push wird geprüft…' : phase !== 'ready' ? 'Push erneut prüfen' : enabled ? 'Push deaktivieren' : 'Push aktivieren';
  return <div className="ae-connect-push">
    <div className="ae-chat-control-row">
    <button type="button" disabled={busy || phase === 'loading'} onClick={toggle} aria-expanded={!supported ? instructions : undefined}>{text(label)}</button>
    <ChatSoundControl />
    </div>
    <p role="status" aria-live="polite">{text(notice || (!supported ? 'Push ist in dieser Ansicht nicht verfügbar. Auf dem iPhone Aeonas zum Home-Bildschirm hinzufügen.' : ''))}</p>
    <details open={phase === 'unsupported' || phase === 'error' || phase === 'unconfigured'} style={{fontSize:11,lineHeight:1.6,marginTop:8,color:'var(--cc-muted)'}}>
      <summary>{english ? 'Push status' : 'Push-Status'}</summary>
      <div>{english ? 'Home screen web app' : 'Home-Bildschirm-Web-App'}: {installed ? (english ? 'Yes' : 'Ja') : (english ? 'No' : 'Nein')}</div>
      <div>HTTPS: {window.isSecureContext ? 'OK' : (english ? 'Missing' : 'Fehlt')}</div>
      <div>Push API: {typeof window.PushManager !== 'undefined' ? 'OK' : (english ? 'Missing' : 'Fehlt')}</div>
      <div>{english ? 'Notifications' : 'Mitteilungen'}: {typeof window.Notification !== 'undefined' ? window.Notification.permission : (english ? 'Unavailable' : 'Nicht verfügbar')}</div>
      <div>Service Worker: {'serviceWorker' in navigator ? workerStatus : (english ? 'Unavailable' : 'Nicht verfügbar')}</div>
      <div>Server: {serverStatus}</div>
      <div role="status">{foregroundStatus}</div>
      {installed && !supported && <p>{english ? 'This web app is installed, but this browser has not exposed the required push features. Check your iOS version and whether Open as Web App was enabled when adding the icon.' : 'Die Web-App ist gestartet, aber benötigte Push-Funktionen fehlen. Bitte iOS-Version und „Als Web-App öffnen“ beim Hinzufügen prüfen.'}</p>}
    </details>
    {instructions && <ol style={{fontSize:12,lineHeight:1.6,paddingLeft:18,color:'var(--cc-muted)'}}>
      <li>{english ? 'Open Share in Safari.' : 'In Safari „Teilen“ öffnen.'}</li>
      <li>{english ? 'Choose Add to Home Screen and enable Open as Web App before adding it.' : '„Zum Home-Bildschirm“ auswählen und vor dem Hinzufügen „Als Web-App öffnen“ aktivieren.'}</li>
      <li>{english ? 'Open Aeonas using the new icon and log in.' : 'Aeonas über das neue Symbol öffnen und anmelden.'}</li>
      <li>{english ? 'In Chat, tap Enable push and allow notifications.' : 'Im Chat „Push aktivieren“ antippen und erlauben.'}</li>
    </ol>}
  </div>;
}
